import { GeneratedZone, Store } from 'store/types/types';
import { getGeneratedZones } from 'manual/selectors';
import { generateZoneId } from 'util/zoneCreation';
import { MAX_SELECTED_LAYERS, NUM_PRODUCTIVITY_BINS, PRODUCTIVITY_BIN_ROUNDING_VALUE } from 'manual/constants';
import { getAxisBins, getAxisBounds } from 'util/formatters/data-viz/axis';
import { getMessage } from '@tcc/i18n';
import COLORS from 'constants/colors';

export const getGeneratedZonesfeatures = (state: Store) => {
  const zones = getGeneratedZones(state);
  const zoneIds = {};
  const summaryZones = { zones: [] };
  const features = Object.keys(zones).map((zone, index) => {
    const zoneIdObj = generateZoneId(zoneIds);
    zoneIds[zoneIdObj] = zoneIdObj;
    const zoneId = Object.keys(zoneIds)[index];
    summaryZones.zones.push({ 
      id: zoneId
    });
    return {
      id: String(index),
      type: 'Feature',
      properties: {
        'zone-id': zoneId,
        product: {},
        rate: {
          q: 0,
          u: ''
        },
        description: String(index + 1)
      },
      geometry: zones[zone].geoJson.geometry
    };  
  });
  return {features, summaryZones};
};

export const getLayersGroupCheckBoxStates = ({
  groupUsableLayersCount = 0,
  selectedGroupLayersCount = 0,
  allSelectedLayersCount = 0,
  selectedMapType
}: {
  groupUsableLayersCount: number;
  selectedGroupLayersCount: number;
  allSelectedLayersCount: number;
  selectedMapType: keyof typeof MAX_SELECTED_LAYERS;
}) => {
  const hasSelectedLayers = selectedGroupLayersCount > 0;
  const isChecked = hasSelectedLayers && selectedGroupLayersCount === groupUsableLayersCount;
  const isIndeterminate = hasSelectedLayers && selectedGroupLayersCount < groupUsableLayersCount;
  const notSelectedLayersOfGroupCount = groupUsableLayersCount - selectedGroupLayersCount;
  const isDisabled
    = (notSelectedLayersOfGroupCount + allSelectedLayersCount > MAX_SELECTED_LAYERS[selectedMapType])
    || groupUsableLayersCount === 0;

  return { isChecked, isDisabled, isIndeterminate };
};

/**
 * Calculates productivity bins from an array of generated zones.
 *
 * @param zones - Array of `GeneratedZone` objects containing geoJson with yield productivity data.
 * @returns An array of tuples representing the lower and upper bounds of each productivity bin.
 */
export const getProductivityBinsFromZones = (zones: GeneratedZone[]) => {
  const zonesProductivityValues = [];
  zones.forEach((zone) => {
    const value = zone?.geoJson?.properties?.relativeProductivity?.v;

    if (value !== null && !isNaN(value)) {
      zonesProductivityValues.push(value);
    }
  });

  const axisBounds = getAxisBounds({ range: zonesProductivityValues });

  return getAxisBins({
    axisBounds,
    binRoundingValue: PRODUCTIVITY_BIN_ROUNDING_VALUE,
    numBins: NUM_PRODUCTIVITY_BINS
  }) as [number, number][];
};

/**
 * Generates display values for productivity zone legends based on bins.
 * 
 * @param params.zones - Array of zones containing yield productivity data
 * @param params.hasMissingDataZone - Boolean indicating if a missing data zone is included
 * 
 * @returns An array of formatted strings representing productivity ranges
 */
export const getDisplayValuesForZonesProductivityLegend = ({
  zones,
  hasMissingDataZone
}: {
  zones: GeneratedZone[];
  hasMissingDataZone: boolean;
}) => {
  const bins = getProductivityBinsFromZones(zones);
  const displayValues = bins
  .map((bin, index) => {
    const [lowerBound, upperBound] = bin;

    if (index === 0) {
      return `< ${upperBound}`;
    } else if (index === bins.length - 1) {
      return `> ${lowerBound}`;
    } else {
      return `${lowerBound} - ${upperBound}`;
    }
  })
  .toReversed();

  if (hasMissingDataZone) {
    displayValues.push(getMessage('label__productivity-legend-missing-data'));
  }

  return displayValues;
};


/**
 * Determines the color for a given relative productivity value based on productivity bins.
 *
 * @param params.zones - Array of zones containing relative productivity data
 * @param params.relativeProductivity - Relative productivity value to determine the color
 *
 * @returns A hex color string representing the corresponding productivity range
 */
export const getColorFromRelativeProductivity = ({
  zones = [],
  relativeProductivity
}: {
  zones: GeneratedZone[];
  relativeProductivity?: number;
}) => {
  if (relativeProductivity == null) {
    return COLORS.productivity[5];
  }

  const color = '#00000000';
  const relativeProductivityPercentage = (relativeProductivity ?? 0);
  const productivityBins = getProductivityBinsFromZones(zones).reverse();
  
  const colorIndex = productivityBins.findIndex(
    ([min, max]) => {
      const adjustedMin = relativeProductivity < 0 ? min - 1 : min;
      const adjustedMax = relativeProductivity > 0 ? max + 1 : max;

      return relativeProductivityPercentage >= adjustedMin && relativeProductivityPercentage <= adjustedMax;
    }
  );
  
  return COLORS.productivity[colorIndex] ?? color;
};
